import type { ComponentProps } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { ResponsiveValue, styleFn } from 'styled-system';
import { height, marginBottom, marginTop, position, space, themeGet, width } from 'styled-system';

import greenCheckmark from '../../assets/icons/green-checkmark.svg';
import redCrossmark from '../../assets/icons/red-crossmark.svg';
import { breakpoint } from '../../theme/helper';
import RichTextBlock from './index';

enum MarginType {
  Top = 'TOP',
  Bottom = 'BOTTOM',
}

interface RichContentProps {
  content: string;
  isCenterAligned?: boolean;
}

const ICON_SIZE = 18;

const setMargins = (marginType: string, marginSize: ResponsiveValue<string | number>) => props =>
  marginType === MarginType.Top
    ? [marginTop({ ...props, marginTop: marginSize })]
    : [marginBottom({ ...props, marginBottom: marginSize })];

interface WithOembedProps {
  (...args: any[]): any;
  oembedProps?: object;
}

const applyCSSWithOembed = (style: styleFn) => (props: WithOembedProps) =>
  style({ ...props, ...props.oembedProps });

const UnstyledContent = styled(RichTextBlock)`
  position: relative;
  overflow-wrap: break-word;

  & > :not(:first-of-type) {
    margin-top: 0;
  }

  & > :not(:last-child) {
    ${setMargins(MarginType.Bottom, ['sp_8', null, 'sp_12'])};
  }

  h1,
  h2,
  h3,
  h4 {
    color: ${themeGet('colors.sys.neutral.text.strong')};

    &:not(:last-child) {
      margin-top: 0;
      ${setMargins(MarginType.Bottom, ['sp_8', null, 'sp_12'])};
    }
    & + p:not(:last-child),
    & + ol:not(:last-child),
    & + ul:not(:last-child) {
      ${setMargins(MarginType.Top, ['sp_8', null, 'sp_12'])};
    }
  }

  p {
    & > span.subheading {
      color: ${themeGet('colors.sys.neutral.text.strong')};
    }
  }

  ol {
    padding-left: 16px;
  }

  ul {
    list-style-type: none;
  }

  ul,
  ol {
    > li {
      position: relative;
      padding-left: 0;
      margin-bottom: 12px;

      &:last-of-type {
        margin-bottom: 0;
      }

      > .highlighted-text {
        margin-top: 8px;
      }
    }
  }

  ul > li::before {
    content: '•';
    font-size: 22px;
  }

  ul > li > span.green-checkmark,
  ul > li > span.red-crossmark {
    padding-left: 8px;
  }

  ul > li > span.green-checkmark::before,
  ul > li > span.red-crossmark::before {
    background-color: white;
    top: 3px;

    ${breakpoint(2)} {
      top: 5px;
    }
  }

  ul > li::before,
  ul > li > span.green-checkmark::before,
  ul > li > span.red-crossmark::before {
    margin-right: 14px;
    position: absolute;
    left: -18px;
  }

  ul > li > span.green-checkmark::before {
    content: '';
    background-image: url(${greenCheckmark});
    background-size: cover;
    height: ${ICON_SIZE}px;
    width: ${ICON_SIZE}px;
  }

  ul > li > span.red-crossmark::before {
    content: '';
    background-image: url(${redCrossmark});
    background-size: cover;
    height: ${ICON_SIZE}px;
    width: ${ICON_SIZE}px;
  }

  .highlighted-text {
    display: block;
    padding: 16px;
    background-color: ${themeGet('colors.sys.neutral.background.weaker')};

    ${breakpoint(0)} {
      padding: 16px 24px;
    }

    ${breakpoint(2)} {
      padding: 20px 24px;
    }
  }

  p.block-img {
    text-align: center;

    & img {
      max-width: 100%;
    }
  }

  [data-oembed-type='video'] {
    ${applyCSSWithOembed(height)};
    ${applyCSSWithOembed(space)};
    display: flex;
    justify-content: center;
    position: relative;
    overflow: hidden;

    iframe {
      ${applyCSSWithOembed(position)};
      ${applyCSSWithOembed(width)};
      height: 100%;
      top: 0;
      left: 0;
    }
  }

  ${({ isCenterAligned }) =>
    isCenterAligned &&
    css`
      text-align: center;

      ul,
      ol {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    `}
`;

const Content = (props: ComponentProps<typeof UnstyledContent>) => (
  <UnstyledContent
    color="sys.neutral.text.default"
    oembedProps={{
      position: ['absolute', 'static'],
      width: [1, '460px', '560px'],
      height: [0, '259px', '315px'],
      my: ['sp_16', 'sp_20', 'sp_24', 'sp_32'],
      pb: ['56.25%', 'sp_0'],
    }}
    {...props}
  />
);

const RichContent = ({ content, ...props }: RichContentProps) => (
  <Content dangerouslySetInnerHTML={{ __html: content }} {...props} />
);

export default RichContent;
